// ─────────────────────────────────────────────────────────────
// SEQ=10110 STEP=4 — 효과음 매니저 (싱글톤)
// SEQ=10213 STEP=1 — WebAudio GainNode 증폭 추가 (HTMLAudio volume max=1.0 한계 우회)
//
// 정책:
//   - HTMLAudio 기반, 사운드당 1 인스턴스 preload
//   - WebAudio API GainNode chain — HTMLAudio → MediaElementSource → GainNode(1.8x) → destination
//   - localStorage 'board.muted' 키로 음소거 영구화 (기기별, 서버 동기화 없음)
//   - muted=true면 play() no-op
//   - 모바일 자동재생 정책 회피: 첫 user interaction 후 unlock() 호출 (audioCtx.resume 동반)
//
// 사용:
//   import { sound } from '@/util/sound'
//   sound.preload()                  // 앱 부팅 시 1회
//   sound.play('dice_roll')          // ID로 재생
//   sound.setMuted(true)             // 음소거 ON
//   sound.isMuted()                  // boolean
//   sound.toggleMuted()              // 토글 (반환 = 적용된 muted 상태)
// ─────────────────────────────────────────────────────────────

export type SoundId = 'dice_roll' | 'my_turn' | 'trade_request'

const SOUND_FILES: Record<SoundId, string> = {
  dice_roll:     '/assets/sound/dice_roll.mp3',
  my_turn:       '/assets/sound/my_turn.mp3',
  trade_request: '/assets/sound/trade_request.mp3',
}

const MUTED_KEY = 'board.muted'

/** SEQ=10213 STEP=1: WebAudio 증폭 배율. HTMLAudio 단독 최대 1.0 → GainNode로 1.8x boost. */
const GAIN_BOOST = 1.8

class SoundManager {
  private cache: Partial<Record<SoundId, HTMLAudioElement>> = {}
  private muted = false
  private unlocked = false
  private preloaded = false
  /** SEQ=10213 STEP=1: WebAudio chain (AudioContext + GainNode). */
  private audioCtx: AudioContext | null = null
  private gainNode: GainNode | null = null

  constructor() {
    try {
      this.muted = localStorage.getItem(MUTED_KEY) === '1'
    } catch {
      this.muted = false
    }
  }

  /** SEQ=10213 STEP=1: WebAudio 컨텍스트 lazy 초기화. preload + unlock 시점 호출. */
  private ensureWebAudio(): void {
    if (this.audioCtx) return
    try {
      const Ctx = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
                  ?? (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctx) return
      this.audioCtx = new Ctx()
      this.gainNode = this.audioCtx.createGain()
      this.gainNode.gain.value = GAIN_BOOST
      this.gainNode.connect(this.audioCtx.destination)
    } catch {
      /* WebAudio 미지원 환경 — HTMLAudio fallback */
    }
  }

  /** SEQ=10213 STEP=1: 단일 HTMLAudio → MediaElementSource → GainNode 연결 (audio당 1회만 허용). */
  private attachGainChain(audio: HTMLAudioElement): void {
    if (!this.audioCtx || !this.gainNode) return
    try {
      const src = this.audioCtx.createMediaElementSource(audio)
      src.connect(this.gainNode)
    } catch {
      /* 이미 연결됐거나 createMediaElementSource 실패 — HTMLAudio 직접 출력 fallback */
    }
  }

  /** 앱 부팅 시 1회 호출. 이미 호출됐으면 멱등. */
  preload(): void {
    if (this.preloaded) return
    this.preloaded = true
    this.ensureWebAudio()
    ;(Object.keys(SOUND_FILES) as SoundId[]).forEach(id => {
      try {
        const audio = new Audio(SOUND_FILES[id])
        audio.preload = 'auto'
        audio.volume  = 1.0
        this.cache[id] = audio
        this.attachGainChain(audio)
      } catch {
        // 환경에 따라 Audio 생성 실패 가능, 무시
      }
    })

    // 첫 사용자 인터랙션 후 unlock — 모바일 자동재생 정책 회피
    const unlock = () => {
      this.unlock()
      window.removeEventListener('click',     unlock)
      window.removeEventListener('touchstart', unlock)
      window.removeEventListener('keydown',   unlock)
    }
    window.addEventListener('click',     unlock, { once: true })
    window.addEventListener('touchstart', unlock, { once: true })
    window.addEventListener('keydown',   unlock, { once: true })
  }

  /** 사용자 인터랙션 후 호출 — 모든 audio를 muted+play 시도하여 unlock */
  private unlock(): void {
    if (this.unlocked) return
    this.unlocked = true
    // SEQ=10213 STEP=1: AudioContext suspended 상태 resume (모바일 정책)
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      try { this.audioCtx.resume() } catch { /* no-op */ }
    }
    ;(Object.keys(this.cache) as SoundId[]).forEach(id => {
      const a = this.cache[id]
      if (!a) return
      const prevMuted = a.muted
      a.muted = true
      a.play().then(() => {
        a.pause()
        a.currentTime = 0
        a.muted = prevMuted
      }).catch(() => {
        a.muted = prevMuted
      })
    })
  }

  /** 효과음 재생. muted=true면 no-op. */
  play(id: SoundId): void {
    if (this.muted) return
    let a = this.cache[id]
    if (!a) {
      // preload 누락 시 즉시 생성
      try {
        a = new Audio(SOUND_FILES[id])
        a.preload = 'auto'
        this.cache[id] = a
        // SEQ=10213 STEP=1: 동적 생성 audio도 GainNode chain 연결
        this.attachGainChain(a)
      } catch {
        return
      }
    }
    try {
      a.currentTime = 0
      a.play().catch(() => {
        // 자동재생 정책으로 실패 — silently ignore
      })
    } catch {
      /* no-op */
    }
  }

  isMuted(): boolean {
    return this.muted
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    try {
      localStorage.setItem(MUTED_KEY, muted ? '1' : '0')
    } catch {
      /* no-op */
    }
    // 즉시 모든 재생 중 음원 정지
    if (muted) {
      ;(Object.keys(this.cache) as SoundId[]).forEach(id => {
        const a = this.cache[id]
        if (a && !a.paused) {
          try { a.pause(); a.currentTime = 0 } catch { /* no-op */ }
        }
      })
    }
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted)
    return this.muted
  }
}

export const sound = new SoundManager()
